/**
 * @param {Boolean} disableSubmitUntilValid
 * */

import Observer from 'utils/Observer';
import FormItem from 'form/FormItem';
import scrollTo from 'utils/scrollTo';
import DOMHelper from 'utils/DOMHelpers';

/**
 * * Class Form that handles base functionality for .....
 * @class
 */
class Form {

    /**
    * Form validation.
    * @constructs
    * @param {HTMLElement} element - The HTMLElement this module is constructed upon
    * @param {Object} options - ConditionerJS's merged options
    */
    constructor(element, options) {

        this._element = element;
        this._options = options || {};

        if (!this._element) {
            return;
        }

        // Class params
        this._formItems = [];

        this._initialize();

    }

    /**
     * Initialize form component
     * @memberof Form
     * @static
     * @private
     */
    _initialize() {

        this._initializeFormItems();

        // find submit buttons
        this._buttons = this._element.querySelectorAll('[type="submit"]');

        // toggle submit
        this._options.disableSubmitUntilValid && this._toggleSubmit();

        // bind events
        this._bindEvents();

    }

    /**
     * Bind events for the module
     * @memberof Form
     * @static
     * @private
     */
    _bindEvents() {

        this._element.addEventListener('submit', e => this._onSubmit(e));
        this._element.addEventListener('invalid', e => this._onInvalid(e));

        // if there is a submit prevent default html5 validation messages and set custom ones
        for (let i = 0, l = this._buttons.length; i < l; i++) {

            let button = this._buttons[i];

            button.addEventListener('click', e => {

                if (!this.isValid()) {
                    e.preventDefault();
                    this._formItems.forEach(formItem => formItem.validate());
                    // scroll to the first invalid element
                    scrollTo(window, DOMHelper.getPosition(this._element.querySelector('[aria-invalid=true]')));

                }

            }, true);
        }

        // attach change listeners when we keep the submit disabled until valid
        this._options.disableSubmitUntilValid && this._formItems.forEach(formItem => {
            Observer.subscribe(formItem, 'change', () => this._toggleSubmit());
        });

    }

    /**
     * Initialize FormItem modules for the inputs
     * @memberof Form
     * @static
     * @private
     */
    _initializeFormItems() {

        let elements = this._element.querySelectorAll('input,select,textarea');

        for (let i = 0, length = elements.length; i < length; i++) {

            let element = elements[i];
            let type = element.getAttribute('type');

            if (type !== 'hidden') {

                let formItem = new FormItem(element);

                this._formItems.push(formItem);

            }
        }
    }

    /**
     * Gets the instance of FormItem related to the element
     * @memberof Form
     * @static
     * @public
     * @param {HTMLElement} element - html element
     * @returns {returnformItem} - the instance of FormItem related to the element
     */
    getFormItemByElement(element) {
        let returnformItem;
        for (let i = 0, length = this._formItems.length; i < length; i++) {

            let formItem = this._formItems[i];

            if (formItem.getItem() === element) {
                returnformItem = formItem;
                break;
            }

        }

        return returnformItem;

    }

    /**
     * Disable or enable submit button
     * @memberof Form
     * @static
     * @private
     */
    _toggleSubmit() {

        let valid = this.isValid();

        // disable submits
        /* for (let button of this._buttons) {
         valid ? button.removeAttribute('disabled') : button.setAttribute('disabled', 'disabled');
         }*/

        for (let i = 0, length = this._buttons.length; i < length; i++) {
            let button = this._buttons[i];
            valid ? button.removeAttribute('disabled') : button.setAttribute('disabled', 'disabled');
        }

    }

    /**
     * Fires when the form is submitted.
     * @memberof Form
     * @static
     * @private
     * @param {event} e - submit event
     */
    _onSubmit(e) {
        let preventDefault = this._options.preventDefault !== true ? false : this._options.preventDefault;
        if (preventDefault) {
            e.preventDefault();
        }
        Observer.publish(this, 'submitForm', this);
    }

    /**
     * Fires when the form is invalid.
     * @memberof Form
     * @static
     * @private
     */
    _onInvalid() {
        Observer.publish(this, 'invalidForm', this);
    }

    /**
     * Checks each form item for its validity
     * @memberof Form
     * @returns {Boolean} Valid state of this form
     * @static
     * @public
     * @param {array} items - array of formitems
     */
    isValid(items = this._formItems) {

        return items.reduce((valid, formItem) => {
            return (formItem.isValid() || formItem.isIgnored()) && valid;
        }, true);

    }

    /**
     * submits form
     * @memberof Form
     * @static
     * @public
     */
    submit() {
        this._element.submit();
    }

    /**
     * gets form
     * @memberof Form
     * @static
     * @public
     * @return {HTMLElement} form - form
     */
    getForm() {
        return this;
    }

    /**
     * gets element
     * @memberof Form
     * @static
     * @public
     * @return {HTMLElement} element - element
     */
    get element() {
        return this._element;
    }

}

export default Form;
